import { Component, computed, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { CardAccount, LoanAccount, SelfServiceableAccounts } from '../../models/accounts.model';
import { AccountsService } from '../../services/accounts.service';
import { HubCardVerificationModalComponent } from '../../../shared/components/hub-card-verification-modal/hub-card-verification-modal.component';
import { LoanAccountSlim } from '../../../../containers/header-nav/header-nav.model';
import { ProfilePaperlessNormalized } from '../../../profile/models/profile-paperless.model';
import { getLoanAccountPaperlessData } from '../../util/accounts.util';
import { selectAutoPay } from '../../../accounts-notifications/store/accounts-notifications.selectors';
import { AutoPayResponse } from '../../../accounts-notifications/models/accounts-notifications.model';

export interface LoanAccountWithAutoPay {
  account: LoanAccount;
  autoPay: AutoPayResponse | undefined;
}

@Component({
  selector: 'app-accounts-v2',
  templateUrl: './accounts.component.html',
  styleUrls: ['./accounts.component.scss'],
  standalone: false,
})
export class AccountsComponent {
  private readonly accountsService = inject(AccountsService);
  private readonly store = inject(Store);

  private readonly logMissingAutoPayApiRows = effect(() => {
    for (const { account, autoPay } of this.loanAccountRows()) {
      if (autoPay === undefined && this.autoPayList() != null) {
        console.error(
          `[app-accounts-v2] No auto-pay API entry for loan account id=${account.id}. Auto-pay row cannot be derived; UI shows loading until a matching entry exists.`
        );
      }
    }
  });

  readonly accounts = input<SelfServiceableAccounts | undefined>(undefined);
  readonly paperlessProfiles = input<ProfilePaperlessNormalized | undefined>(undefined);

  private readonly autoPayList = toSignal(this.store.select(selectAutoPay), {
    initialValue: undefined as AutoPayResponse[] | undefined,
  });

  readonly loanAccounts = computed(() => this.accounts()?.loanAccounts[0]?.accountDetailsList ?? []);
  readonly cardAccounts = computed(() => this.accounts()?.cardAccounts ?? []);
  readonly loanUrl = computed(() => this.accounts()?.loanAccounts[0]?.url ?? '');
  readonly pastDueLoanAccount = computed(() => this.accounts()?.pastDueLoanAccount);

  readonly loanAccountRows = computed((): LoanAccountWithAutoPay[] => {
    const loanAccountList = this.loanAccounts();
    const autoPayList = this.autoPayList();
    return loanAccountList.map((account) => ({
      account,
      autoPay: autoPayList == null ? undefined : autoPayList.find((row) => row.accountId === account.id),
    }));
  });

  clickCreditCard(card: CardAccount) {
    this.accountsService.navigateToCreditCard(card, HubCardVerificationModalComponent);
  }

  clickLoanCard(loanAccount: LoanAccount) {
    const paperless = this.paperlessProfiles();
    const { repossessionCaseStatus, reinstatedDate } = getLoanAccountPaperlessData(loanAccount, paperless!);
    const loanAccountSlim: LoanAccountSlim = {
      url: this.loanUrl(),
      productType: loanAccount.productType,
      accountStatus: loanAccount.accountStatus,
      last4DigitsExternalAccountNumber: loanAccount.last4DigitsExternalAccountNumber,
      id: loanAccount.id,
      repossessionCaseStatus: repossessionCaseStatus,
      reinstatedDate: reinstatedDate,
    };

    this.pastDueLoanAccount()
      ? this.accountsService.navigateToPersonalLoan(loanAccountSlim)
      : this.accountsService.directLinkToPersonalLoan(loanAccountSlim);
  }

  clickLoanMakePayment(loanAccount: LoanAccount) {
    const paperless = this.paperlessProfiles();
    const { repossessionCaseStatus, reinstatedDate } = getLoanAccountPaperlessData(loanAccount, paperless!);
    const loanAccountSlim: LoanAccountSlim = {
      url: this.loanUrl(),
      productType: loanAccount.productType,
      accountStatus: loanAccount.accountStatus,
      last4DigitsExternalAccountNumber: loanAccount.last4DigitsExternalAccountNumber,
      id: loanAccount.id,
      repossessionCaseStatus: repossessionCaseStatus,
      reinstatedDate: reinstatedDate,
    };
    this.pastDueLoanAccount() && this.pastDueLoanAccount()?.id !== loanAccount.id
      ? this.accountsService.navigateToPersonalLoan(loanAccountSlim)
      : this.accountsService.navigateToPersonalLoanPayment(loanAccountSlim);
  }

  clickChangeDueDate(loanAccount: LoanAccount) {
    this.accountsService.navigateToPersonalLoanChangeDueDate(this.loanUrl(), loanAccount.id?.toString());
  }
}
